import { useEffect, useRef, useState } from 'react'
import type { FC } from 'react'
import { Box, Breadcrumbs, Button, Typography } from '@mui/material'
import { Link, useParams } from 'react-router'
import { useReactToPrint } from 'react-to-print'
import type { PatientUser } from '~/shared/types'
import FormSubmissionOverview from './FormSubmissionOverview'
import FormTasks from './FormTasks'
import EmbeddedFormSubmission from './EmbeddedFormSubmission'
import { formSubmission, FormSubmissionRetrieveResponse } from '@fireflyhealth/api'
import { fireAndForget } from '~/shared/lib/fireAndForget'

const PRINT_PAGE_STYLE =
  '@page { size: auto;  margin: .5in; } @media print { body { -webkit-print-color-adjust: exact; } }'

interface FormSubmissionProps {
  user: PatientUser
}

// A type alias, not an interface: aliases get the implicit index signature
// that `useParams`' constraint requires.
type FormSubmissionParams = {
  id: string
  submissionId: string
}

const FormSubmission: FC<FormSubmissionProps> = props => {
  const { submissionId, id } = useParams<FormSubmissionParams>()
  const printContentRef = useRef<HTMLDivElement | null>(null)
  const printFormSubmission = useReactToPrint({
    contentRef: printContentRef,
    pageStyle: PRINT_PAGE_STYLE,
  })

  const [submission, setSubmission] = useState<FormSubmissionRetrieveResponse | null>(null)

  useEffect(() => {
    const fetchSubmission = async () => {
      if (!submissionId) return
      const { data } = await formSubmission.retrieve({ id: Number(submissionId) })
      setSubmission(data)
    }
    fireAndForget('[Forms] load submission', fetchSubmission)
  }, [submissionId])

  return submission ? (
    <Box sx={{ overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Breadcrumbs style={{ flex: 1 }}>
          <Link to={`/patients/${id}/forms`}>All Forms</Link>
          <Typography>{submission.form.title}</Typography>
        </Breadcrumbs>
        {submission.vendor === 'Firefly' ? (
          <Button onClick={() => printFormSubmission()}>Print</Button>
        ) : null}
      </Box>
      {submission.task_relations ? (
        <FormTasks taskIds={submission.task_relations.map(r => r.task_id)} />
      ) : null}

      {submission.vendor === 'Firefly' ? (
        <div ref={printContentRef}>
          <FormSubmissionOverview submission={submission} user={props.user} />
        </div>
      ) : (
        <EmbeddedFormSubmission formSubmission={submission} />
      )}
    </Box>
  ) : null
}

export default FormSubmission
